package com.vizio.redwolf.pay.api

import com.vizio.redwolf.utils.ECommerceEnvironmentConfig
import com.vizio.redwolf.pay.client.SubscriptionHttpClient
import com.vizio.redwolf.pay.model.CreateOrder
import com.vizio.redwolf.pay.model.CreateOrderInfo
import com.vizio.redwolf.pay.model.CreateTransactionInfo
import com.vizio.redwolf.pay.model.OrderPreview
import com.vizio.redwolf.pay.model.OrderPreviewInfo
import com.vizio.redwolf.pay.model.PartnerPlansInfo
import com.vizio.redwolf.pay.model.Plan
import com.vizio.redwolf.pay.model.PreviewSubscriptionInfo
import com.vizio.redwolf.pay.model.PurchaseSubscriptionInfo
import com.vizio.redwolf.pay.model.Subscription
import com.vizio.redwolf.pay.model.SubscriptionInvoice
import com.vizio.redwolf.pay.model.SubscriptionInvoiceSummary
import com.vizio.redwolf.pay.model.SubscriptionItemPreview
import com.vizio.redwolf.pay.model.SubscriptionResult
import com.vizio.redwolf.pay.model.TransactionOrder
import com.vizio.redwolf.pay.model.UpgradePlan
import com.vizio.redwolf.pay.model.V2GetInvoiceById
import com.vizio.redwolf.pay.model.V2GetInvoices
import com.vizio.redwolf.pay.model.handleSubscriptionHttpResult
import com.vizio.redwolf.utils.AppConfig
import com.vizio.redwolf.utils.ClientConfig
import com.vizio.redwolf.utils.PayEnvironment
import com.vizio.redwolf.utils.ResponseHeaders
import com.vizio.redwolf.utils.choosePayEnvironment
import com.vizio.redwolf.utils.clientprovider.ClientConfigFactory
import com.vizio.redwolf.utils.clientprovider.WebApiUseCase
import com.vizio.redwolf.utils.model.VersionResponse
import com.vizio.redwolf.utils.retryRequest
import io.ktor.http.HttpStatusCode

/**
 * For swagger refer - https://app-vizio-ecommerce-subscriptions-dev.azurewebsites.net/swagger/index.html
 */
class SubscriptionService(payEnv: PayEnvironment, private val appConfig: AppConfig, private val clientConfig: ClientConfig = ClientConfigFactory.getClientConfig(WebApiUseCase.COOKIE_SDK_HEADERS)) : SubscriptionServiceInterface {

    private val subscriptionClient = SubscriptionHttpClient(payEnv = payEnv, appConfig = appConfig, clientConfig = clientConfig)

    constructor(envConfig: ECommerceEnvironmentConfig, appConfig: AppConfig) : this(choosePayEnvironment(envConfig), appConfig, ClientConfigFactory.getClientConfig(WebApiUseCase.COOKIE_SDK_HEADERS))
    constructor(envConfig: ECommerceEnvironmentConfig, appConfig: AppConfig, clientConfig: ClientConfig) : this(choosePayEnvironment(envConfig), appConfig, clientConfig)
    constructor(envConfig: ECommerceEnvironmentConfig, appConfig: AppConfig, timeout: Long) : this(choosePayEnvironment(envConfig), appConfig, ClientConfigFactory.getClientConfig(WebApiUseCase.COOKIE_SDK_HEADERS, timeout))

    companion object {
        const val ERROR_CONTENT_TYPE_NOT_SUPPORTED = "NoTransformationFoundException: Response ContentType is not supported"
    }

    /**
     * Preview a subscription with coupon_code and/or address details
     * If a preview address is not provided, the default address will be used
     * to calculate plan preview details
     */
    override suspend fun previewSubscription(token: String, previewSubscriptionInfo: PreviewSubscriptionInfo, sentryHeader: String?, tvAppId: String?): SubscriptionResult<SubscriptionItemPreview> {
        return retrySubscriptionRequest {
            val httpResult = subscriptionClient.previewSubscription(token, previewSubscriptionInfo, sentryHeader, tvAppId)
            handleSubscriptionHttpResult(httpResult)
        }
    }

    override suspend fun purchaseSubscription(token: String, planCode: String, addOnCodes: List<String>?, couponCode: String?, sentryHeader: String?, tvAppId: String?): SubscriptionResult<Subscription> {
        return retrySubscriptionRequest {
            val purchaseSubscriptionInfo = PurchaseSubscriptionInfo(planCode, addOnCodes, couponCode)
            val httpResult = subscriptionClient.purchaseSubscription(token, purchaseSubscriptionInfo, sentryHeader, tvAppId)
            handleSubscriptionHttpResult(httpResult)
        }
    }

    override suspend fun createTransaction(token: String, transactionInfo: CreateTransactionInfo, sentryHeader: String?, tvAppId: String?): SubscriptionResult<TransactionOrder> {
        return retrySubscriptionRequest {
            val httpResult = subscriptionClient.createTransaction(token, transactionInfo, sentryHeader, tvAppId)
            handleSubscriptionHttpResult(httpResult)
        }
    }

    override suspend fun createOrder(token: String, createOrderInfo: CreateOrderInfo, sentryHeader: String?, tvAppId: String?): SubscriptionResult<CreateOrder> {
        return retrySubscriptionRequest {
            val httpResult = subscriptionClient.createOrder(token, createOrderInfo, sentryHeader, tvAppId)
            handleSubscriptionHttpResult(httpResult)
        }
    }

    override suspend fun orderPreview(token: String, orderPreviewInfo: OrderPreviewInfo, sentryHeader: String?, tvAppId: String?): SubscriptionResult<OrderPreview> {
        return retrySubscriptionRequest {
            val httpResult = subscriptionClient.orderPreview(token, orderPreviewInfo, sentryHeader, tvAppId)
            handleSubscriptionHttpResult(httpResult)
        }
    }

    override suspend fun getSubscriptions(token: String, sentryHeader: String?, tvAppId: String?): SubscriptionResult<List<Subscription>> {
        return retrySubscriptionRequest {
            val httpResult = subscriptionClient.getSubscriptions(token, sentryHeader, tvAppId)
            handleSubscriptionHttpResult(httpResult)
        }
    }

    /**
     * Gets a specific Subscription for a specific plan code
     *
     * @param token The ping access token
     * @param planCode The planCode for the desired subscription
     * @param sentryHeader The standardized sentry header used for tracking
     * @param tvAppId The standardized sentry header used for tracking
     * @return [SubscriptionResult] Returns a result that contains the standard subscription object
     */
    override suspend fun getSubscriptionsWithPlanCode(token: String, planCode: String, sentryHeader: String?, tvAppId: String?): SubscriptionResult<Subscription> {
        return retrySubscriptionRequest {
            val httpResult = subscriptionClient.getSubscriptionsWithPlanCode(token, planCode, sentryHeader, tvAppId)
            handleSubscriptionHttpResult(httpResult)
        }
    }

    override suspend fun reactivateSubscription(token: String, subscriptionId: String, sentryHeader: String?, tvAppId: String?): SubscriptionResult<Subscription> {
        return retrySubscriptionRequest {
            val httpResult = subscriptionClient.reactivateSubscription(token, subscriptionId, sentryHeader, tvAppId)
            handleSubscriptionHttpResult(httpResult)
        }
    }

    override suspend fun cancelSubscription(token: String, subscriptionId: String, sentryHeader: String?, tvAppId: String?): SubscriptionResult<Subscription> {
        return retrySubscriptionRequest {
            val httpResult = subscriptionClient.cancelSubscription(token, subscriptionId, sentryHeader, tvAppId)
            handleSubscriptionHttpResult(httpResult)
        }
    }

    /**
     * Terminate subscription completely wipes/deletes the subscription for an account.
     * This should NOT be used interchangeably/confused with cancel subscription.
     * Clients should not use this request without a valid reason
     *
     * @param token The ping access token
     * @param subscriptionId The id of the subscription to be terminated
     * @param sentryHeader The standardized sentry header used for tracking
     * @param tvAppId The standardized sentry header used for tracking
     * @return [SubscriptionResult] Returns a result that contains the standard subscription object
     */
    override suspend fun terminateSubscription(token: String, subscriptionId: String, sentryHeader: String?, tvAppId: String?): SubscriptionResult<Subscription> {
        return retrySubscriptionRequest {
            val httpResult = subscriptionClient.terminateSubscription(token, subscriptionId, sentryHeader, tvAppId)
            handleSubscriptionHttpResult(httpResult)
        }
    }

    override suspend fun getV2Invoices(token: String, pageNumber: Int?, pageSize: Int?, sentryHeader: String?, tvAppId: String?): SubscriptionResult<V2GetInvoices> {
        return retrySubscriptionRequest {
            val httpResult = subscriptionClient.getV2Invoices(token, pageNumber, pageSize, sentryHeader, tvAppId)
            handleSubscriptionHttpResult(httpResult)
        }
    }

    override suspend fun getV2InvoiceById(token: String, invoiceId: String, sentryHeader: String?, tvAppId: String?): SubscriptionResult<V2GetInvoiceById> {
        return retrySubscriptionRequest {
            val httpResult = subscriptionClient.getV2InvoiceById(token, invoiceId, sentryHeader, tvAppId)
            handleSubscriptionHttpResult(httpResult)
        }
    }

    /**
     * If all orders requested, will return 0-200 order summaries
     * Results ordered by most recent billingDate
     */
    override suspend fun getAccountInvoices(token: String, year: Int?, month: Int?, sentryHeader: String?, tvAppId: String?): SubscriptionResult<List<SubscriptionInvoiceSummary>> {
        return retrySubscriptionRequest {
            val httpResult = subscriptionClient.getAccountInvoiceSummaries(token, year, month, sentryHeader, tvAppId)
            handleSubscriptionHttpResult(httpResult)
        }
    }

    override suspend fun getAccountInvoice(token: String, invoiceId: String, sentryHeader: String?, tvAppId: String?): SubscriptionResult<SubscriptionInvoice> {
        return retrySubscriptionRequest {
            val httpResult = subscriptionClient.getAccountInvoice(token, invoiceId, sentryHeader, tvAppId)
            handleSubscriptionHttpResult(httpResult)
        }
    }

    override suspend fun getPartnerIdPlans(partnerPlansInfo: PartnerPlansInfo, sentryHeader: String?, tvAppId: String?): SubscriptionResult<List<Plan>> {
        return retrySubscriptionRequest {
            val httpResult = subscriptionClient.getPartnerIdPlans(partnerPlansInfo, sentryHeader, tvAppId)

            when (val subscriptionResult: SubscriptionResult<List<Plan>> = handleSubscriptionHttpResult(httpResult)) {
                is SubscriptionResult.NotFound -> SubscriptionResult.Success(ResponseHeaders.Valid(HttpStatusCode.NotFound.value), listOf())
                else -> subscriptionResult
            }
        }
    }

    override suspend fun getPlan(planCode: String, sentryHeader: String?, tvAppId: String?): SubscriptionResult<Plan> {
        return retrySubscriptionRequest {
            val httpResult = subscriptionClient.getPlan(planCode, sentryHeader, tvAppId)
            handleSubscriptionHttpResult(httpResult)
        }
    }

    override suspend fun getPlanUpgrades(authToken: String, planCode: String, sentryHeader: String?, tvAppId: String?): SubscriptionResult<List<UpgradePlan>> {
        return retrySubscriptionRequest {
            val httpResult = subscriptionClient.getPlanUpgrades(authToken, planCode, sentryHeader, tvAppId)
            handleSubscriptionHttpResult(httpResult)
        }
    }

    override suspend fun getVersion(): SubscriptionResult<VersionResponse> {
        return retrySubscriptionRequest {
            val getVersionResponse = subscriptionClient.getVersion()
            handleSubscriptionHttpResult(getVersionResponse)
        }
    }

    private fun <T: Any> isSubscriptionSuccess(result: T): Boolean {
        return result is SubscriptionResult.Success<*>
    }

    private suspend inline fun <T: Any> retrySubscriptionRequest(block: () -> T): T {
        return retryRequest(clientConfig, block, isSuccess = ::isSubscriptionSuccess)
    }

}
